import { CompanyService } from './../company/company.service';
import { map, mergeMap, delay, tap } from 'rxjs/operators';
import { Observable, of, forkJoin } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@api/services/api/api.service';
import {
  PositionDTO,
  WizardDTO,
  FormDTO,
  DomainListDTO,
  FormUpdateDTO,
  DomainItemDTO,
  WizardStepNextOutputDTO,
  ChecklistDTO,
  ReportRequestDTO,
  QuotationParamsDTO,
  DigitalSigningDTO,
  BankAccountLinkDTO,
  BankAccountEvaluationDTO,
  CreditUseListDTO,
  AuthenticateDTO,
  NoteDTO,
  ApiParams,
  UserDataDTO,
  QuotationBoundariesDTO,
  SurveyDTO,
  SurveyUpdateDTO,
  ChannelDTO,
  OcrCheckDTO,
  OcrScanDTO
} from '@core/modules/api/models';
import { OutputAdapterService } from '@core/modules/api/services/output-adapter/output-adapter.service';
import { InputAdapterService } from '@core/modules/api/services/input-adapter/input-adapter.service';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiCustomService extends ApiService {
  constructor(
    protected http: HttpClient,
    protected outputAdapter: OutputAdapterService,
    protected inputAdapter: InputAdapterService,
    private companyService: CompanyService
  ) {
    super(http, outputAdapter, inputAdapter);
  }

  postSecurityLogon(params: ApiParams<void, AuthenticateDTO>): Observable<any> {
    if (!this.isRealEnv()) {
      return of({});
    }

    const url = `${this.securityUrl}/authenticate`;

    return this.http.post<any>(url, params.body, params.options);
  }

  getSecurityUserData(params: ApiParams<void, void>): Observable<UserDataDTO> {
    if (!this.isRealEnv()) {
      return of(JSON.parse(sessionStorage.getItem('dev_userdata'))).pipe(
        map(res => this.outputAdapter.getSecurityUserData(res))
      );
    }

    const url = `${this.securityUrl}/userdata`;

    return this.http
      .get<UserDataDTO>(
        url,
        this.inputAdapter.getSecurityUserData(params).options
      )
      .pipe(map(res => this.outputAdapter.getSecurityUserData(res)));
  }

  getDomain(
    params: ApiParams<{ code: string }, void>
  ): Observable<DomainListDTO> {
    const code = params.pathParams.code;
    let filter: string;

    if (params.options.params instanceof HttpParams) {
      filter = params.options.params.get('filter');
    }

    if (code === 'LEGAL_LINKS') {
      return this.getResCollection<DomainItemDTO>('domains_LEGAL_LINKS').pipe(
        map(res => ({ domainList: res }))
      );
    } else if (code === 'ONBOARDING_STEPS') {
      return this.getResCollection<DomainItemDTO>(
        'domains_ONBOARDING_STEPS'
      ).pipe(map(res => ({ domainList: res })));
    } else if (code === 'PRINT_DOCUMENTS' && filter === 'pre-contr') {
      return this.getResCollection<DomainItemDTO>(
        'domains_PRINT_DOCUMENTS_pre-contr'
      ).pipe(map(res => ({ domainList: res })));
    } else {
      return super.getDomain(params);
    }
  }

  getPosition(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}`;

    return forkJoin([
      this.http.get<PositionDTO>(url, params.options),
      this.getPositionCustomer(params),
      this.getPositionQuotation(params)
    ]).pipe(
      map(([position, customer, quotation]) => ({
        positionId: position.positionId,
        correspondenceSendingMethod: position.correspondenceSendingMethod,
        preEvaluationType: position.preEvaluationType,
        ndg: customer.ndg,
        customer,
        product: {
          ...quotation,
          positionId: params.pathParams.positionId,
          correspondenceSendingMethod: position.correspondenceSendingMethod
        },
        creationTool: position.creationTool
      }))
    );
  }

  getChannel(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChannelDTO> {
    return of({ channel: null });
  }

  getPreWizard(
    params: ApiParams<{ wizardCode: string }, void>
  ): Observable<WizardDTO> {
    return of({
      applCode: 'DGL',
      businessId: 'f2a9d4ff-4c56-49b3-8bdd-7d31f3753531',
      status: 4,
      steps: [
        {
          parentStepCode: 'SELF-DIGITAL',
          status: '2',
          stepCode: 'SELF-WAIT-START',
          steps: []
        },
        {
          parentStepCode: 'SELF-WAIT-START',
          status: '0',
          stepCode: 'SELF-SIMULATION',
          steps: []
        },
        {
          parentStepCode: 'SELF-SIMULATION',
          status: '0',
          stepCode: 'SELF-ONBOARDING',
          steps: []
        },
        {
          groupCod: 'GROUP_A',
          parentStepCode: 'SELF-ONBOARDING',
          status: '0',
          stepCode: 'SELF-EMPLOYMENT',
          steps: []
        },
        {
          groupCod: 'GROUP_A',
          parentStepCode: 'SELF-EMPLOYMENT',
          status: '0',
          stepCode: 'SELF-PROOF-OF-INCOME',
          steps: []
        },
        {
          groupCod: 'GROUP_A',
          parentStepCode: 'SELF-PROOF-OF-INCOME',
          status: '0',
          stepCode: 'SELF-ADDITIONAL-DATA',
          steps: []
        },
        {
          groupCod: 'GROUP_A',
          parentStepCode: 'SELF-ADDITIONAL-DATA',
          status: '0',
          stepCode: 'SELF-WAIT-SECCI',
          steps: []
        },
        {
          groupCod: 'GROUP_A',
          parentStepCode: 'SELF-WAIT-SECCI',
          status: '0',
          stepCode: 'SELF-QUOTATION-CONFIRM',
          steps: []
        },
        {
          parentStepCode: 'SELF-QUOTATION-CONFIRM',
          status: '0',
          stepCode: 'SELF-CONTRACT-SIGN',
          steps: []
        },
        {
          parentStepCode: 'SELF-CONTRACT-SIGN',
          status: '4',
          stepCode: 'SELF-RELEASE',
          steps: []
        }
      ],
      wizardCode: 'SELF-DIGITAL',
      wizardId: null
    }).pipe(map(res => this.outputAdapter.getPreWizard(res)));
  }

  getWizard(
    params: ApiParams<{ positionId: string; wizardCode: string }, void>
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}`;

    return this.http
      .get<WizardDTO>(url, this.inputAdapter.getWizard(params).options)
      .pipe(map(res => this.outputAdapter.getWizard(res)));
  }

  getForm(
    params: ApiParams<{ positionId: string; formCode: string }, void>
  ): Observable<FormDTO[]> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .get<FormDTO[]>(url, this.inputAdapter.getForm(params).options)
      .pipe(
        map(res => this.outputAdapter.getForm(res)),
        tap(() => {
          setTimeout(() => {
            this.setAriaHiddenOnSpecificFormElements(
              params.pathParams.formCode
            );
          });
        })
      );
  }

  postForm(
    params: ApiParams<{ positionId: string; formCode: string }, FormUpdateDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postForm(params).body,
        this.inputAdapter.postForm(params).options
      )
      .pipe(map(res => this.outputAdapter.postForm(res)));
  }

  getSurvey(
    params: ApiParams<{ positionId: string; surveyCode: string }, void>
  ): Observable<SurveyDTO[]> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;
    const options = this.inputAdapter.getSurvey(params).options;

    if (options.params instanceof HttpParams) {
      options.params = options.params.set('create', 'true');
    }

    return this.http
      .get<any>(url, this.inputAdapter.getSurvey(params).options)
      .pipe(map(res => this.outputAdapter.getSurvey(res)));
  }

  putSurvey(
    params: ApiParams<
      { positionId: string; surveyCode: string },
      SurveyUpdateDTO
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putSurvey(params).body,
        this.inputAdapter.putSurvey(params).options
      )
      .pipe(map(res => this.outputAdapter.putSurvey(res)));
  }

  putWizardStepNext(
    params: ApiParams<
      { positionId: string; wizardCode: string; stepCode: string },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}/steps/${params.pathParams.stepCode}/next`;

    return this.http
      .put<WizardStepNextOutputDTO>(
        url,
        this.inputAdapter.putWizardStepNext(params).body,
        this.inputAdapter.putWizardStepNext(params).options
      )
      .pipe(map(res => this.outputAdapter.putWizardStepNext(res)));
  }

  getChecklist(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChecklistDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/checklists/current`;

    return this.http
      .get<ChecklistDTO>(url, this.inputAdapter.getChecklist(params).options)
      .pipe(map(res => this.outputAdapter.getChecklist(res)));
  }

  postChecklistDocumentUpload(
    params: ApiParams<{ positionId: string; docId: string }, FormData>
  ): Observable<number> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file`;

    return this.http
      .post<number>(
        url,
        this.inputAdapter.postChecklistDocumentUpload(params).body,
        this.inputAdapter.postChecklistDocumentUpload(params).options
      )
      .pipe(map(res => this.outputAdapter.postChecklistDocumentUpload(res)));
  }

  putChecklistDocumentOverride(
    params: ApiParams<
      { positionId: string; docId: string; fileId: string },
      FormData
    >
  ): Observable<number> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file`;

    return this.http
      .post<number>(
        url,
        this.inputAdapter.postChecklistDocumentUpload(params).body,
        this.inputAdapter.postChecklistDocumentUpload(params).options
      )
      .pipe(map(res => this.outputAdapter.postChecklistDocumentUpload(res)));
  }

  getChecklistDocumentScan(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrScanDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/scan`;

    return this.http
      .put<OcrScanDTO>(
        url,
        {},
        this.inputAdapter.getChecklistDocumentScan(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentScan(res)));
  }

  getChecklistDocumentCheckOcr(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrCheckDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/check-ocr`;

    return this.http
      .put<OcrCheckDTO>(
        url,
        {},
        this.inputAdapter.getChecklistDocumentCheckOcr(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentCheckOcr(res)));
  }

  postReport(
    params: ApiParams<
      { positionId: string; reportCode: string },
      ReportRequestDTO
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/reports/${params.pathParams.reportCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postReport(params).body,
        this.inputAdapter.postReport(params).options
      )
      .pipe(map(res => this.outputAdapter.postReport(res)));
  }

  putPositionQuotation(
    params: ApiParams<{ positionId: string }, QuotationParamsDTO>
  ): Observable<void> {
    const url1 = `${this.apiUrl}/v1/quotations`;

    return this.http
      .post<any>(
        url1,
        this.inputAdapter.postQuotations({
          body: params.body,
          options: params.options
        }).body,
        this.inputAdapter.postQuotations({
          body: params.body,
          options: params.options
        }).options
      )
      .pipe(
        mergeMap(res1 => {
          const url2 = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/quotation`;

          return this.http
            .put<void>(
              url2,
              res1.quotationData[0],
              this.inputAdapter.putPositionQuotation(params).options
            )
            .pipe(map(res2 => this.outputAdapter.putPositionQuotation(res2)));
        })
      );
  }

  getDigitalSignatureRequestDsp(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/sign`;

    return this.http
      .post<DigitalSigningDTO>(
        url,
        {},
        this.inputAdapter.getDigitalSignatureRequestDsp(params).options
      )
      .pipe(map(res => this.outputAdapter.getDigitalSignatureRequestDsp(res)));
  }

  postPsd2Start(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountLinkDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/psd2`;

    return this.http
      .post<BankAccountLinkDTO>(
        url,
        this.inputAdapter.postPsd2Start(params).body,
        this.inputAdapter.postPsd2Start(params).options
      )
      .pipe(map(res => this.outputAdapter.postPsd2Start(res)));
  }

  getPsd2Verify(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountEvaluationDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/psd2`;

    return this.http
      .get<BankAccountEvaluationDTO>(
        url,
        this.inputAdapter.getPsd2Verify(params).options
      )
      .pipe(map(res => this.outputAdapter.getPsd2Verify(res)));
  }

  getPositionQuotationBoundaries(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationBoundariesDTO> {
    return this.getQuotationsBoundaries({
      options: params.options
    });
  }

  getNotes(
    params: ApiParams<{ positionId: string; type: string }, void>
  ): Observable<NoteDTO[]> {
    return of([
      {
        noteId: 0,
        positionId: '11111',
        requestId: '111111',
        noteType: 'Tipologia di nota',
        noteText: 'Testo',
        insertDate: 1669798017302,
        insertUser: 'Mario',
        deletion: 'Cancellazione'
      }
    ]);
  }

  getQuotationCreditUse(
    params: ApiParams<{ mode: string }, void>
  ): Observable<CreditUseListDTO> {
    return of({
      uses: [
        {
          amount: 100
        }
      ]
    });
  }

  private getResCollection<T>(code: string): Observable<T[]> {
    return of(this.companyService.getResCollection<T>(code)).pipe(delay(150));
  }

  private isRealEnv(): boolean {
    return (
      !!environment.demoDatasetId ||
      (!location.origin.includes('localhost:') &&
        !location.origin.includes('credem-sviluppo'))
    );
  }

  private setAriaHiddenOnSpecificFormElements(formCode: string): void {
    if (formCode !== 'SELF-FINALITY') {
      return;
    }

    const iconEl = document.querySelector('#field-INFO_LOAN_PURPOSE ion-icon');
    const pEl = document.querySelector('#field-INFO_LOAN_PURPOSE p');

    if (pEl) {
      pEl.setAttribute('aria-hidden', 'true');
    }

    if (iconEl) {
      iconEl.setAttribute('aria-hidden', 'true');
    }
  }
}
